<script lang="ts" setup>
import {IconPosition} from "~/enums/IconPositionEnum";

const localePath = useLocalePath();

const props = defineProps(
    {
      text: {type: String, required: true},
      redirect: {type: String, required: true},
      icon: {type: String, required: false},
      target: {type: String, required: false, default: '_self'},
      iconPosition: {type: String, required: false, default: IconPosition.Left},
      height: {type: String, required: false, default: '3.5rem'},
      iconSize: {type: String, required: false, default: '1.5rem'},
    }
)

const shouldShowIcon = computed(() => props.icon)

// A new tab is both a security concern (reverse tabnabbing) and a surprise
// worth announcing, so both are handled wherever this button opens one.
const opensNewTab = computed(() => props.target === '_blank')

// localePath() drops "#hash" fragments from plain path strings,
// so split them out and pass a route object instead.
const to = computed(() => {
  const [path, hash] = props.redirect.split('#');
  return hash ? localePath({path, hash: `#${hash}`}) : localePath(path);
})

const cssVars = computed(() => ({
  '--height': props.height,
}))
</script>

<template>
  <nuxt-link :to="to" class="refuge-btn" :target="target" :style="cssVars"
             :rel="opensNewTab ? 'noopener noreferrer' : undefined">
    <Icon v-if="shouldShowIcon" :name="icon ?? ''" :size="iconSize" aria-hidden="true"
          :class="iconPosition === IconPosition.Right && 'order-last'"/>
    {{ text }}
    <span v-if="opensNewTab" class="sr-only">({{ $t('opensInNewTab') }})</span>
  </nuxt-link>
</template>

<style lang="sass" scoped>
.refuge-btn
  display: inline-flex
  justify-content: center
  align-items: center
  gap: 0.5rem
  padding: 0 2rem
  font-weight: 600
  font-size: 1rem
  height: var(--height, 3.5rem)
  cursor: pointer
  transition: all $transition-standard
  border: 2px solid $color-dark-bg
  border-radius: $border-radius-btn
  text-decoration: none
  color: $color-dark-bg
  width: fit-content
  background-color: transparent

  &:hover
    color: white
    background-color: $color-dark-bg
    transform: translateY(-2px)
    box-shadow: $shadow-modern

  .order-last
    order: 1

.dark-mode .refuge-btn
  border: 2px solid white
  color: white

  &:hover
    background-color: white
    color: black
</style>
